import React, { useState, useRef } from 'react';
import { Box, Tab, Typography, Avatar, FormHelperText } from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  Crop as CropIcon,
} from '@mui/icons-material';
import ImageCropper from '../ImageCropper';
import BadgePreview from '../BadgePreview';
import Button from '../UIKit/Button';
import Tabs from '../UIKit/Tabs';
import TextField from '../UIKit/TextField';
import { Card } from '../UIKit';
import { useTranslation } from 'react-i18next';

const ImageSelector = ({
  isEdit = false,
  badge,
  handleChange,
  imagePreview,
  setImagePreview,
  imageFile,
  setImageFile,
  imageUploadTab,
  setImageUploadTab,
  formErrors,
  setFormErrors,
  getRarityColor,
}) => {
  const { t } = useTranslation(['badges', 'common']);
  const fileInputRef = useRef(null);

  // Состояния для кроппера изображений
  const [showCropper, setShowCropper] = useState(false);
  const [imageForCrop, setImageForCrop] = useState(null);

  // Обработка переключения вкладок для загрузки изображения
  const handleImageTabChange = (event, newValue) => {
    setImageUploadTab(newValue);
    // Сбрасываем файл при переключении на вкладку URL
    if (newValue === 1) {
      setImageFile(null);
    }
  };

  // Обработчик завершения кроппинга
  const handleCropComplete = (croppedImageUrl, croppedImageFile) => {
    setImagePreview(croppedImageUrl);
    setImageFile(croppedImageFile);
    setShowCropper(false);
  };

  // Обработчик отмены кроппинга
  const handleCancelCrop = () => {
    setShowCropper(false);
  };

  // Обработчик загрузки файла с предварительной подготовкой для кроппинга
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Проверка типа файла
    if (!file.type.startsWith('image/')) {
      setFormErrors({
        ...formErrors,
        image: t('badges:imageSelector.errors.invalidType'),
      });
      return;
    }

    // Проверка размера файла (не более 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setFormErrors({
        ...formErrors,
        image: t('badges:imageSelector.errors.sizeExceeded'),
      });
      return;
    }

    // Создаем предпросмотр изображения для кроппинга
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageDataUrl = e.target.result;
      setImageForCrop(imageDataUrl);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  // Обработка выбора файла через кнопку
  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  return (
    <Card>
      <Box
        sx={{
          bgcolor: 'background.default',
          borderRadius: { xs: '12px', sm: '16px' },
          p: '4px',
          width: '100%',
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <Tabs
          value={imageUploadTab}
          onChange={handleImageTabChange}
          aria-label="image upload tabs"
        >
          <Tab
            icon={<CloudUploadIcon />}
            label={t('badges:imageSelector.tabs.upload')}
          />
          <Tab icon={<LinkIcon />} label={t('badges:imageSelector.tabs.url')} />
        </Tabs>
      </Box>

      {showCropper && imageForCrop ? (
        <ImageCropper
          image={imageForCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCancelCrop}
          aspectRatio={1}
        />
      ) : (
        <>
          {imageUploadTab === 0 && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleFileUpload}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={handleChooseFile}
                sx={{ mb: 2, mr: 1 }}
              >
                {t('badges:imageSelector.buttons.chooseImage')}
              </Button>
              {imagePreview && (
                <Button
                  variant="outlined"
                  startIcon={<CropIcon />}
                  onClick={() => {
                    setImageForCrop(imagePreview);
                    setShowCropper(true);
                  }}
                  sx={{ mb: 2 }}
                >
                  {t('badges:imageSelector.buttons.crop')}
                </Button>
              )}
              {formErrors.image && imageUploadTab === 0 && (
                <FormHelperText error>{formErrors.image}</FormHelperText>
              )}
            </Box>
          )}

          {imageUploadTab === 1 && (
            <TextField
              name="image"
              label={t('badges:imageSelector.imageUrl')}
              fullWidth
              value={badge?.image || ''}
              onChange={handleChange}
              error={!!formErrors.image && imageUploadTab === 1}
              helperText={
                formErrors.image && imageUploadTab === 1 ? formErrors.image : ''
              }
              sx={{ mb: 2 }}
            />
          )}
        </>
      )}

      {(imagePreview || badge?.image) && !showCropper && (
        <>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('common:preview')}:
            </Typography>
            <Avatar
              src={imagePreview || badge.image}
              alt={t('badges:imageSelector.badgePreview')}
              sx={{
                width: 100,
                height: 100,
                margin: '0 auto',
                border: `2px solid ${getRarityColor(badge.rarity)}`,
              }}
            />
          </Box>

          <BadgePreview
            imageSrc={imagePreview || badge.image}
            badgeTitle={badge.name || t('badges:imageSelector.newBadge')}
            rarity={badge.rarity}
            tier={badge.tier}
          />
        </>
      )}
    </Card>
  );
};

export default ImageSelector;
